/* eslint-disable lines-between-class-members */
import { ScriptableObject, ScriptableObjects } from "./Base.js";

// List of mutable properties surfaced by AINode.
const AI_PROPS = [
  "inputType",
  "apiKey",
  "encrypted",
  "enableOnProximity",
  "proximityRadius",
  "aiName",
  "userPrompts",
  "welcomeText",
  "welcomeTextUrl",
  "linkedModel",
  "emitEvents",
  "voiceSpeechStrategy",
  "leaveColliderText",
  "provider",
  "persistable",
  // provider-specific
  "assistant", // OpenAI assistant ID
  "endpoint", // Azure AI
  "model", // OpenAI completion / Azure AI
  "systemPrompt"
];

/* -------------------------------------------------------------------------- */
/*  Editor adapter                                                             */
/* -------------------------------------------------------------------------- */

class AIAgentEditorAdapter extends ScriptableObject {
  constructor(obj) {
    super(obj);
    AI_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this.obj?.[prop],
        set: v => {
          if (!this.obj) return;
          this.obj[prop] = v;
        }
      });
    });
  }
}

/* -------------------------------------------------------------------------- */
/*  A-Frame adapter                                                            */
/* -------------------------------------------------------------------------- */

class AIAgentAFrameAdapter extends ScriptableObject {
  constructor(obj) {
    super(obj);
    const COMPONENT = "ai-agent";

    const setProp = (key, val) => {
      if (!this.obj?.el) return;
      const current = { ...(this.obj.el.getAttribute(COMPONENT) || {}) };
      current[key] = val;
      this.obj.el.setAttribute(COMPONENT, current);
    };
    const getProp = key => this.obj?.el?.getAttribute(COMPONENT)?.[key];

    AI_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getProp(prop),
        set: v => setProp(prop, v)
      });
    });
  }
}

/* -------------------------------------------------------------------------- */
/*  ScriptableObject wrapper                                                   */
/* -------------------------------------------------------------------------- */

export class AIAgentScriptableObject extends ScriptableObject {
  constructor(obj) {
    super(obj);
    this._adapter =
      ScriptableObject.runtimeMode === "aframe" || obj?.el
        ? new AIAgentAFrameAdapter(obj)
        : new AIAgentEditorAdapter(obj);
  }

  static _trackedProps = AI_PROPS;

  static _ensureProxy() {
    if (this._propsProxied) return;
    this._propsProxied = true;
    for (const p of this._trackedProps) {
      if (p in this.prototype) continue;
      Object.defineProperty(this.prototype, p, {
        get() {
          return this._adapter?.[p];
        },
        set(v) {
          if (this._adapter) this._adapter[p] = v;
        }
      });
    }
  }
}

AIAgentScriptableObject._ensureProxy();

/* -------------------------------------------------------------------------- */
/*  Convenience creators                                                       */
/* -------------------------------------------------------------------------- */

function _applyInitialProps(inst, initial) {
  Object.keys(initial).forEach(k => {
    if (k in inst) inst[k] = initial[k];
  });
}

export class AIAgentElementAFrame extends AIAgentScriptableObject {
  constructor(initial = {}) {
    if (typeof document === "undefined") {
      super({});
      return;
    }
    const el = document.createElement("a-entity");
    el.setAttribute("ai-agent", {
      aiName: "AI Agent ",
      provider: "OpenAI Assistant",
      inputType: "text-audio",
      encrypted: false,
      apiKey: "",
      enableOnProximity: false,
      proximityRadius: 3
    });
    const created = { el, scriptableType: "ai-agent" };
    super(created);
    _applyInitialProps(this, initial);
  }
}

export class AIAgentElementEditor extends AIAgentScriptableObject {
  constructor(initial = {}) {
    /* eslint-disable no-undef */
    const NodeCtor =
      (typeof globalThis !== "undefined" &&
        (globalThis.AINode || globalThis.Object3D)) ||
      undefined;
    if (typeof NodeCtor !== "function") {
      throw new Error("AIAgentElementEditor: AINode constructor not found");
    }
    const node = new NodeCtor(ScriptableObject.editor || {});
    node.scriptableType = "ai-agent";
    super(node);
    _applyInitialProps(this, initial);
  }
}

export class AIAgentElement {
  constructor(initial = {}) {
    return ScriptableObject.runtimeMode === "aframe"
      ? new AIAgentElementAFrame(initial)
      : new AIAgentElementEditor(initial);
  }
}

ScriptableObjects.registerType(
  "ai-agent",
  obj => new AIAgentScriptableObject(obj)
);
